import React, { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
} from '@fluentui/react-components';
import {
  Home20Regular,
  Megaphone20Regular,
  Server20Regular,
  Bug20Regular,
} from '@fluentui/react-icons';
import { loginRequest } from './authConfig';
import { useAuth } from './hooks/useAuth';
import { useIsAdmin } from './hooks/useIsAdmin';
import AppHeader from './components/AppHeader';
import LoginCard from './components/LoginCard';
import Navigation from './components/Navigation';
import MainContent from './components/MainContent';
import SwaggerPage from './components/SwaggerPage';
import SessionExpiredModal from './components/SessionExpiredModal';
import LegoIcon from './components/LegoIcon';
import PolarisIcon from './components/PolarisIcon';
import APIIcon from './components/APIIcon';
import AdminIcon from './components/AdminIcon';
import AnnouncementPage from './components/AnnouncementPage';
import PolarisMachinesPage from './components/PolarisMachinesPage';
import PolarisJobDetailPage from './components/PolarisJobDetailPage';
import { resolveSectionAccent } from './theme/legoTheme';

const DEFAULT_DOCUMENT_TITLE = 'Lego Portal';

const getPolarisPageSubtitle = (pageId) => {
  if (pageId === 'polaris') return 'Polaris';
  if (pageId === 'polaris/submit') return 'Polaris · Submit Job';
  if (pageId === 'polaris/test-types') return 'Polaris · Test Types';
  if (pageId === 'polaris/test-types/create') return 'Polaris · Create Test Type';
  if (pageId.startsWith('polaris/job/')) {
    return `Polaris · Job ${pageId.slice('polaris/job/'.length)}`;
  }
  if (pageId.startsWith('polaris/test-types/')) {
    return `Polaris · Test Type ${pageId.slice('polaris/test-types/'.length)}`;
  }
  return null;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  mainContainer: {
    display: 'flex',
    flex: 1,
    width: '100%',
    overflow: 'hidden',
  },
  contentWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  breadcrumbContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    flexShrink: 0,
  },
  breadcrumbButton: {
    ...shorthands.border('none'),
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
});

function App() {
  const classes = useStyles();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  
  // 使用全局 Auth Context 来管理 session 过期状态
  const { sessionExpired, handleLogout: authHandleLogout } = useAuth();
  const isAdmin = useIsAdmin();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // 从 URL 路径确定当前页面
  const getPageFromPath = (pathname) => {
    if (pathname === '/' || pathname === '/home') return 'home';
    if (pathname.startsWith('/polaris/submit')) return 'polaris/submit';
    if (pathname.startsWith('/polaris/job/')) {
      const jobId = decodeURIComponent(pathname.split('/').slice(3).join('/'));
      return `polaris/job/${jobId}`;
    }
    if (pathname.startsWith('/polaris/test-types/create')) return 'polaris/test-types/create';
    if (pathname.startsWith('/polaris/test-types/')) {
      const testTypeId = decodeURIComponent(pathname.split('/').slice(3).join('/'));
      return `polaris/test-types/${testTypeId}`;
    }
    if (pathname.startsWith('/polaris/test-types')) return 'polaris/test-types';
    if (pathname.startsWith('/polaris')) return 'polaris';
    const page = pathname.slice(1);
    return page || 'home';
  };

  const selectedPage = getPageFromPath(location.pathname);
  const polarisPageSubtitle = getPolarisPageSubtitle(selectedPage);

  useEffect(() => {
    document.title = polarisPageSubtitle
      ? `${DEFAULT_DOCUMENT_TITLE} - ${polarisPageSubtitle.replaceAll(' · ', ' - ')}`
      : DEFAULT_DOCUMENT_TITLE;
  }, [polarisPageSubtitle]);

  // 页面切换处理函数
  const handlePageChange = (pageId) => {
    if (pageId === 'polaris') {
      const currentUserAlias = accounts[0]?.username?.split('@')[0] || '';
      const polarisPath = currentUserAlias
        ? `/polaris?user=${encodeURIComponent(currentUserAlias)}`
        : '/polaris';
      navigate(polarisPath);
      return;
    }
    navigate(`/${pageId === 'home' ? '' : pageId}`);
  };

  const handleLogin = async () => {
    try {
      // 检查是否已经有交互正在进行
      if (inProgress !== 'none') {
        console.log('Interaction already in progress:', inProgress);
        console.log('Attempting to clear stale interaction state...');
        
        // 自动清除旧的交互状态并重试
        sessionStorage.clear();
        
        // 等待一小段时间让 MSAL 内部状态更新
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 重新加载页面以完全重置 MSAL 状态
        window.location.reload();
        return;
      }

      console.log('Starting login with redirect...');
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      // Log detailed error information for debugging (server-side logging in production)
      console.error('Login failed:', e);
      
      // 处理 interaction_in_progress 错误
      if (e.errorCode === 'interaction_in_progress') {
        console.log('Detected interaction_in_progress error, clearing state and retrying...');
        // 清除会话存储中的交互状态
        sessionStorage.clear();
        
        // 自动重新加载页面以重置状态
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        // Display generic user-safe error message without exposing internal details
        alert('Login failed. Please try again or contact support if the issue persists.');
      }
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // 传递当前页面路径给 authHandleLogout，以便重新登录后返回
      await authHandleLogout(location.pathname + location.search);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // 使用统一 hook 判断是否 admin
  const userAccount = accounts[0];

  // 基础菜单项
  const baseMenuItems = [
    { id: 'home', label: 'Lego', icon: <LegoIcon style={{ fontSize: '20px' }} />, color: '#243a5e' },
    { id: 'polaris', label: 'Polaris', icon: <PolarisIcon style={{ fontSize: '20px' }} />, color: '#1e69f5' },
    { id: 'swagger', label: 'API', icon: <APIIcon style={{ fontSize: '20px' }} />, color: '#f34f1c' },
  ];

  // 如果是 admin 用户，添加 Admin 分组菜单项
  const menuItems = isAdmin
    ? [
        ...baseMenuItems,
        {
          id: 'admin',
          label: 'Admin Tools',
          icon: <AdminIcon style={{ fontSize: '20px' }} />,
          color: '#8661c5',
          children: [
            { id: 'admin/announcement', label: 'Announcement', icon: <Megaphone20Regular />, color: '#8661c5' },
            { id: 'admin/polaris-machines', label: 'Polaris Machines', icon: <Server20Regular />, color: '#8661c5' },
            { id: 'admin/debug', label: 'Debug', icon: <Bug20Regular />, color: '#8661c5' },
          ],
        },
      ]
    : baseMenuItems;

  if (!isAuthenticated) {
    return (
      <div className={classes.root}>
        <div className={classes.mainContainer}>
          <div className={classes.contentWrapper}>
            <MainContent selectedPage="home" isAuthenticated={false} onLogin={handleLogin} />
          </div>
        </div>
      </div>
    );
  }

  // 查找当前页面及其父级，支持多级面包屑
  const findCurrentPageWithParent = (items, pageId) => {
    for (const item of items) {
      if (item.id === pageId) {
        return { page: item, parents: [] };
      }
      if (item.children) {
        const found = item.children.find(child => child.id === pageId);
        if (found) {
          return { page: found, parents: [item] };
        }
      }
    }
    // Handle deep sub-pages like polaris/test-types/BaaS
    if (pageId.startsWith('polaris/test-types/')) {
      const testTypeId = pageId.slice('polaris/test-types/'.length);
      const polarisItem = items.find(item => item.id === 'polaris');
      return {
        page: { id: pageId, label: testTypeId, icon: polarisItem?.icon, color: polarisItem?.color },
        parents: [
          polarisItem,
          { id: 'polaris/test-types', label: 'Test Types', icon: polarisItem?.icon, color: polarisItem?.color },
        ].filter(Boolean),
      };
    }
    if (pageId.startsWith('polaris/job/')) {
      const jobId = pageId.slice('polaris/job/'.length);
      const polarisItem = items.find(item => item.id === 'polaris');
      return {
        page: { id: pageId, label: jobId, icon: polarisItem?.icon, color: polarisItem?.color },
        parents: [polarisItem].filter(Boolean),
      };
    }
    // Fall back to parent for sub-pages (e.g. polaris/test-types -> polaris)
    const parentId = pageId.split('/')[0];
    if (parentId !== pageId) {
      const parentItem = items.find(item => item.id === parentId);
      if (parentItem) {
        return { page: { ...parentItem, label: pageId.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }, parents: [parentItem] };
      }
    }
    return { page: null, parents: [] };
  };

  const { page: currentPage, parents: parentPages } = findCurrentPageWithParent(menuItems, selectedPage);
  // Always use root menu item title in header
  const getRootMenuItem = () => {
    const rootId = selectedPage.split('/')[0];
    return menuItems.find(item => item.id === rootId);
  };
  const rootMenuItem = getRootMenuItem();
  const pageTitle = 'Lego';
  const pageSubtitle = polarisPageSubtitle
    || (rootMenuItem && rootMenuItem.label !== 'Lego' ? rootMenuItem.label : '');
  const headerIcon = rootMenuItem?.icon;
  const headerAccent = resolveSectionAccent(selectedPage);

  return (
    <div className={classes.root}>
      <AppHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        icon={headerIcon}
        userAccount={userAccount}
        onLogout={handleLogout}
        accentColor={headerAccent}
      />

      <div className={classes.mainContainer}>
        <Navigation
          menuItems={menuItems}
          selectedPage={selectedPage}
          onSelectPage={handlePageChange}
          isExpanded={isNavExpanded}
          onToggleExpanded={() => setIsNavExpanded(!isNavExpanded)}
        />

        <div className={classes.contentWrapper}>
          <div className={classes.breadcrumbContainer}>
            <Breadcrumb>
              {parentPages.map((parent, index) => (
                <React.Fragment key={parent.id}>
                  <BreadcrumbItem>
                    <BreadcrumbButton
                      icon={index === 0 ? parent.icon : undefined}
                      className={classes.breadcrumbButton}
                      onClick={() => handlePageChange(parent.id)}
                    >
                      {parent.label}
                    </BreadcrumbButton>
                  </BreadcrumbItem>
                  <BreadcrumbDivider />
                </React.Fragment>
              ))}
              <BreadcrumbItem>
                <BreadcrumbButton icon={parentPages.length === 0 ? currentPage?.icon : undefined} current className={classes.breadcrumbButton}>
                  {currentPage?.label}
                </BreadcrumbButton>
              </BreadcrumbItem>
            </Breadcrumb>
          </div>

          <Routes>
            <Route path="/" element={<MainContent selectedPage="home" isAuthenticated={true} />} />
            <Route path="/home" element={<MainContent selectedPage="home" isAuthenticated={true} />} />
            <Route path="/polaris" element={<MainContent selectedPage="polaris" isAuthenticated={true} />} />
            <Route path="/polaris/submit" element={<MainContent selectedPage="polaris/submit" isAuthenticated={true} />} />
            <Route path="/polaris/job/:jobId" element={<PolarisJobDetailPage />} />
            <Route path="/polaris/test-types" element={<MainContent selectedPage="polaris/test-types" isAuthenticated={true} />} />
            <Route path="/polaris/test-types/create" element={<MainContent selectedPage="polaris/test-types/create" isAuthenticated={true} />} />
            <Route path="/polaris/test-types/:testTypeId" element={<MainContent selectedPage="polaris/test-types" isAuthenticated={true} />} />
            <Route path="/swagger" element={<MainContent selectedPage="swagger" isAuthenticated={true} />} />
            {isAdmin && <Route path="/admin/announcement" element={<AnnouncementPage />} />}
            {isAdmin && <Route path="/admin/polaris-machines" element={<PolarisMachinesPage />} />}
            {isAdmin && <Route path="/admin/debug" element={<MainContent selectedPage="admin/debug" isAuthenticated={true} />} />}
          </Routes>
        </div>
      </div>
      
      {/* 全局 Session 过期模态框 - 不可被关闭 */}
      <SessionExpiredModal
        open={sessionExpired}
        onLogout={handleLogout}
        isLoading={isLoggingOut}
      />
    </div>
  );
}

export default App;